import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { adminService } from '../../services/adminService';
import { Plan, Subscription, User } from '../../types';

const LIMIT_FIELDS = [
  'campaigns',
  'projects',
  'keywords',
  'competitors',
  'content_generations',
  'team_members',
  'automated_workflows'
] as const;

type LimitKey = typeof LIMIT_FIELDS[number];

// Maps each backend field to the i18n key under `admin.*`. Adding a new limit
// field requires both registering it here AND adding the i18n key in pt/en.
const LIMIT_LABEL_KEY: Record<LimitKey, string> = {
  campaigns: 'admin.campaigns',
  projects: 'admin.projects',
  keywords: 'admin.keywords',
  competitors: 'admin.competitors',
  content_generations: 'admin.contentGenerations',
  team_members: 'admin.teamMembers',
  automated_workflows: 'admin.automatedWorkflows'
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Either user or subscription must be provided. */
  user?: Pick<User, 'id' | 'email' | 'name'> & { subscriptions?: (Subscription & { plan?: Plan })[]; subscription?: Subscription & { plan?: Plan } };
  subscription?: Subscription & { plan?: Plan; user?: Pick<User, 'id' | 'email' | 'name'> };
  plans: Plan[];
}

/**
 * Reusable modal that mirrors AdminCompaniesPage's "Assign Plan" form, exposed
 * to other admin screens (Users, Subscriptions). Falls back to
 * `assignPlanToUser` if a user is supplied, or `changeSubscriptionPlan` if a
 * subscription is supplied — both endpoints share the same payload shape.
 */
const AssignPlanModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, user, subscription, plans }) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const initialSub = subscription || user?.subscription || user?.subscriptions?.[0];
  const initialCustomLimits = (initialSub?.metadata as any)?.custom_limits || {};

  const buildInitialForm = () => {
    const customLimits: Record<LimitKey, string> = {} as Record<LimitKey, string>;
    LIMIT_FIELDS.forEach((f) => {
      const v = initialCustomLimits[f];
      customLimits[f] = v !== undefined && v !== null ? String(v) : '';
    });
    return {
      plan_id: initialSub?.plan_id || '',
      billing_cycle: (initialSub?.billing_cycle || 'monthly') as 'monthly' | 'yearly',
      is_free: !!initialSub?.metadata?.is_free,
      custom_price: initialSub?.metadata?.custom_price?.toString() || '',
      expires_at: initialSub?.current_period_end ? new Date(initialSub.current_period_end).toISOString().split('T')[0] : '',
      notes: initialSub?.metadata?.admin_notes || '',
      custom_limits: customLimits
    };
  };

  const [form, setForm] = useState(buildInitialForm);

  // Re-seed when the modal opens with a different subject.
  useEffect(() => {
    if (isOpen) setForm(buildInitialForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subscription?.id, user?.id]);

  const targetLabel = useMemo(() => {
    const u = subscription?.user || user;
    return u ? `${u.name || u.email} (${u.email})` : '';
  }, [user, subscription]);

  const handleSubmit = async () => {
    if (!form.plan_id) {
      toast.error(t('admin.selectPlanPlaceholder'));
      return;
    }
    setIsSaving(true);
    try {
      const customLimitsPayload: Record<string, number> = {};
      Object.entries(form.custom_limits).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) {
          const num = Number(v);
          if (!Number.isNaN(num)) customLimitsPayload[k] = num;
        }
      });
      const payload = {
        plan_id: form.plan_id,
        billing_cycle: form.billing_cycle,
        is_free: form.is_free,
        custom_price: form.custom_price ? parseFloat(form.custom_price) : undefined,
        expires_at: form.expires_at || undefined,
        notes: form.notes || undefined,
        custom_limits: Object.keys(customLimitsPayload).length > 0 ? customLimitsPayload : undefined
      };

      let response;
      if (subscription?.id) {
        response = await adminService.changeSubscriptionPlan(subscription.id, payload);
      } else if (user?.id) {
        response = await adminService.assignPlanToUser({ user_id: user.id, ...payload });
      } else {
        toast.error(t('errors.generic'));
        return;
      }

      if (response.success) {
        toast.success(t('admin.planAssigned'));
        onClose();
        onSuccess?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const planOptions = useMemo(
    () => [
      { value: '', label: t('admin.selectPlanPlaceholder') },
      ...plans.map((p) => ({ value: p.id, label: p.name }))
    ],
    [plans, t]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={subscription ? t('admin.changePlanTitle') : t('admin.assignPlanTitle')}
      size="2xl"
      maxHeight
    >
      <div className="space-y-5">
        {targetLabel && (
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg text-sm text-gray-700 dark:text-gray-200">
            <span className="font-medium">{t('admin.user')}: </span>
            {targetLabel}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label={t('admin.selectPlan')}
            options={planOptions}
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
            required
          />
          <Select
            label={t('admin.billingCycle')}
            options={[
              { value: 'monthly', label: t('admin.billingCycleMonthly') },
              { value: 'yearly', label: t('admin.billingCycleYearly') }
            ]}
            value={form.billing_cycle}
            onChange={(e) => setForm({ ...form, billing_cycle: e.target.value as 'monthly' | 'yearly' })}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="assign-is-free"
            type="checkbox"
            checked={form.is_free}
            onChange={(e) => setForm({ ...form, is_free: e.target.checked })}
            className="w-4 h-4"
          />
          <label htmlFor="assign-is-free" className="text-sm text-gray-700 dark:text-gray-300">
            {t('admin.isFree')}
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('admin.customPrice')}
            placeholder={t('admin.customPricePlaceholder') || ''}
            type="number"
            step="0.01"
            value={form.custom_price}
            onChange={(e) => setForm({ ...form, custom_price: e.target.value })}
            disabled={form.is_free}
            helperText={t('admin.customPriceHint') || ''}
          />
          <Input
            label={t('admin.expiresAt')}
            type="date"
            value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          />
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('admin.planLimits')}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('admin.limitsHint')}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LIMIT_FIELDS.map((field) => (
              <Input
                key={field}
                label={t(LIMIT_LABEL_KEY[field])}
                type="number"
                value={form.custom_limits[field]}
                onChange={(e) => setForm({ ...form, custom_limits: { ...form.custom_limits, [field]: e.target.value } })}
                placeholder="—"
              />
            ))}
          </div>
        </div>

        <div>
          <Input
            label={t('admin.notes')}
            placeholder={t('admin.notesPlaceholder') || ''}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !form.plan_id} loading={isSaving}>
            {subscription ? t('admin.changePlan') : t('admin.assignPlan')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AssignPlanModal;
